import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  AI_REPORT_TYPES,
  AiReport,
  AiReportListItem,
  AiReportParams,
  AiReportType,
  AiReportsService,
} from 'src/app/shared/services/ai-reports.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { AiReportFormComponent } from '../ai-report-form/ai-report-form.component';
import { AiReportViewComponent } from '../ai-report-view/ai-report-view.component';

@Component({
  selector: 'app-ai-reports-page',
  standalone: true,
  imports: [CommonModule, AiReportFormComponent, AiReportViewComponent],
  templateUrl: './ai-reports-page.component.html',
  styleUrls: ['./ai-reports-page.component.css'],
})
export class AiReportsPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  readonly types = AI_REPORT_TYPES;
  activeType: AiReportType = 'daily';
  preset: Partial<AiReportParams> | null = null;
  projectId: number | null = null;

  history: AiReportListItem[] = [];
  historyLoading = false;
  report: AiReport | null = null;
  loadingReport = false;
  generating = false;

  constructor(
    private reports: AiReportsService,
    private toastr: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    const type = q.get('type') as AiReportType | null;
    if (type && this.types.some((t) => t.type === type)) this.activeType = type;
    const num = (k: string) => {
      const v = Number(q.get(k));
      return Number.isFinite(v) && v > 0 ? v : null;
    };
    this.projectId = num('projectId');
    this.preset = {
      projectId: this.projectId ?? undefined,
      boqId: num('boqId'),
      bomId: num('bomId'),
      date: q.get('date'),
      from: q.get('from'),
      to: q.get('to'),
    };
    const reportId = num('reportId');
    if (reportId) this.open(reportId);
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeMeta() {
    return this.types.find((t) => t.type === this.activeType)!;
  }

  selectType(type: AiReportType): void {
    if (type === this.activeType) return;
    this.activeType = type;
    this.report = null;
    this.syncQuery({ type, reportId: null });
    this.loadHistory();
  }

  onProjectChange(projectId: number | null): void {
    this.projectId = projectId;
    this.syncQuery({ projectId, reportId: null });
    this.loadHistory();
  }

  loadHistory(): void {
    this.historyLoading = true;
    this.reports
      .list({ type: this.activeType, projectId: this.projectId, limit: 50 })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.historyLoading = false)))
      .subscribe({
        next: (rows) => (this.history = rows),
        error: () => {
          this.history = [];
          this.toastr.error('Failed to load report history');
        },
      });
  }

  generate(params: AiReportParams): void {
    this.generating = true;
    this.reports
      .generate(params)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.generating = false)))
      .subscribe({
        next: (report) => {
          this.report = report;
          this.syncQuery({ reportId: report.id });
          this.toastr.success(report.ai_used ? 'AI report generated' : 'Report generated (AI off)');
          this.loadHistory();
        },
        error: (err) => this.toastr.error(err?.error?.message || 'Failed to generate report'),
      });
  }

  regenerate(): void {
    if (!this.report) return;
    this.generate({ ...this.reports.paramsFromReport(this.report), useAi: true });
  }

  open(id: number): void {
    if (this.report?.id === id) return;
    this.loadingReport = true;
    this.reports
      .get(id)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loadingReport = false)))
      .subscribe({
        next: (report) => {
          this.report = report;
          if (report.report_type !== this.activeType) {
            this.activeType = report.report_type;
            this.loadHistory();
          }
          this.syncQuery({ reportId: id, type: report.report_type });
        },
        error: () => this.toastr.error('Report not found'),
      });
  }

  remove(item: AiReportListItem, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    this.reports.remove(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.history = this.history.filter((h) => h.id !== item.id);
        if (this.report?.id === item.id) {
          this.report = null;
          this.syncQuery({ reportId: null });
        }
        this.toastr.success('Report deleted');
      },
      error: () => this.toastr.error('Failed to delete report'),
    });
  }

  private syncQuery(patch: Record<string, any>): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: patch, queryParamsHandling: 'merge', replaceUrl: true });
  }
}
